/**
 * Code of this file is greatly helped by react-sortable-hoc
 * https://github.com/clauderic/react-sortable-hoc/blob/162857bd5acc1c6bb5f7e50f95bdf95f7f52a58a/src/SortableContainer/index.js
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2016, Claudéric Demers
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import React from "react";
import {withTranslation} from "react-i18next";
import {connect} from "react-redux";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import ConfigIcon from "@material-ui/icons/Settings";
import UndoIcon from "@material-ui/icons/Undo";
import RedoIcon from "@material-ui/icons/Redo";
import AddIcon from "@material-ui/icons/Add";
import DownIcon from "mdi-react/ArrowDownIcon";
import ToBottomIcon from "mdi-react/ArrowCompressDownIcon";
import UpIcon from "mdi-react/ArrowUpIcon";
import ToTopIcon from "mdi-react/ArrowCompressUpIcon";
import CloseIcon from "mdi-react/CloseCircleIcon";
import Chip from "@material-ui/core/Chip";
import Manager, {GeneralGroup, GeneralItem, ItemBase} from "./Manager";
import ConfirmDialog from "../ConfirmDialog";
import {withScrollAPI} from "../../../module/ContentSizeProvider/ScrollProvider";
import {events} from "./utils";
import SortableItem from "./SortableItem";
import {
  animateNodes as _animateNodes,
  autoScroll as _autoScroll,
  cancel as _cancel,
  getContainer as _getContainer,
  getLockPixelOffsets as _getLockPixelOffsets,
  handleEnd as _handleEnd,
  handleMove as _handleMove,
  handlePress as _handlePress,
  handleSortEnd as _handleSortEnd,
  handleSortMove as _handleSortMove,
  handleStart as _handleStart,
  updatePosition as _updatePosition,
} from "./sortUtils";
import NameEditor from "./NameEditor";
import {Containers, eventKey, GroupEditorProps, GroupEditorState, Vars} from "./types";
import * as A from "../../../actions";
import withStyles, {StyleRulesCallback} from "@material-ui/core/styles/withStyles";
import {ThunkDispatch} from "redux-thunk";
import {RootState} from "../../../reducers";
import {CustomTheme} from "../../theme";
import {Omit, TReservedHocProps} from "../../type";


class GroupEditor extends React.Component<GroupEditorProps, GroupEditorState> {
  public manager = new Manager(this.props);
  public events = {
    start: this.handleStart.bind(this),
    move: this.handleMove.bind(this),
    end: this.handleEnd.bind(this),
  };
  public vars: Vars = {
    _touched: false,
    _pos: null,
    pressTimer: undefined,
    cancelTimer: undefined,
    node: null,
    margin: null,
    width: null,
    height: null,
    marginOffset: null,
    boundingClientRect: null,
    containerBoundingRect: null,
    offsetEdge: null,
    initialOffset: null,
    initialScroll: null,
    initialWindowScroll: null,
    helper: null,
    sortableGhost: null,
    minTranslate: {x: 0, y: 0},
    maxTranslate: {x: 0, y: 0},
    listenerNode: null,
    autoScrollInterval: null,
    translate: null,
    isAutoScrolling: null,
    groupsOffset: {},
    animateTimer: null,
  };
  public containers: Containers = {
    container: null,
    document: null,
    contentWindow: null,
    scrollContainer: null,
  };
  public sortableItemRefs: Array<{item: ItemBase, ref: React.RefObject<SortableItem>}> = [];
  public hideSortableGhost: boolean = true;
  
  
  constructor(props: GroupEditorProps){
    super(props);
    
    this.getDialog = this.getDialog.bind(this);
    this.inspectGroupAreaOffset = this.inspectGroupAreaOffset.bind(this);
    this.getGroupIdAt = this.getGroupIdAt.bind(this);
    this.getNodeByItemId = this.getNodeByItemId.bind(this);
    this.removeHighlightFromGroups = this.removeHighlightFromGroups.bind(this);
    this.onChangeItemName = this.onChangeItemName.bind(this);
    this.onClickRemoveItem = this.onClickRemoveItem.bind(this);
    this.onClickRemoveDialogOK = this.onClickRemoveDialogOK.bind(this);
    this.onClickRemoveDialogCancel = this.onClickRemoveDialogCancel.bind(this);
    this.onKeyDownOnWindow = this.onKeyDownOnWindow.bind(this);
    this.onAddItem = this.onAddItem.bind(this);
    this.undo = this.undo.bind(this);
    this.redo = this.redo.bind(this);
    this.onChangeGroupName = this.onChangeGroupName.bind(this);
    this.onClickRemoveGroup = this.onClickRemoveGroup.bind(this);
    this.onClickRemoveGroupDialogOK = this.onClickRemoveGroupDialogOK.bind(this);
    this.onClickAddGroup = this.onClickAddGroup.bind(this);
    this.onClickUp = this.onClickUp.bind(this);
    this.onClickDown = this.onClickDown.bind(this);
    this.onClickTop = this.onClickTop.bind(this);
    this.onClickBottom = this.onClickBottom.bind(this);
    this.onSelectCategory = this.onSelectCategory.bind(this);
    this.onChangeCurrency = this.onChangeCurrency.bind(this);
  
    this.handleStart = this.handleStart.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handlePress = this.handlePress.bind(this);
    this.cancel = this.cancel.bind(this);
    this.handleSortMove = this.handleSortMove.bind(this);
    this.handleSortEnd = this.handleSortEnd.bind(this);
    this.getLockPixelOffsets = this.getLockPixelOffsets.bind(this);
    this.updatePosition = this.updatePosition.bind(this);
    this.animateNodes = this.animateNodes.bind(this);
    this.autoScroll = this.autoScroll.bind(this);
    this.getContainer = this.getContainer.bind(this);
    
    this.manager.active = null;
    
    const groups = this.manager.getGroupsWithChildren();
    
    this.state = {
      error: "",
      groups,
      dialogOpen: "",
      removingItemIds: [],
      removingGroupIds: [],
      confirmRemove: true,
    };
  }
  
  public render(){
    const {
      classes,
      lang,
      i18n,
      width,
      maxGroupNameLength,
      maxItemNameLength,
      addGroupLabel,
      categories,
      bindPropCategory,
      currencies,
    } = this.props;
    const {groups, dialogOpen} = this.state;
    const t = i18n.getFixedT(lang, "general");
    
    this.sortableItemRefs = [];
    
    const dialog = this.getDialog(dialogOpen);
    
    return (
      <div className={classes.root} style={{width}}>
        <div className={classes.toolbar}>
          <IconButton
            title={t<string>("undo")}
            disabled={this.manager.undoStates.length < 1}
            onClick={this.undo}
          >
            <UndoIcon fontSize="small"/>
          </IconButton>
          <IconButton
            title={t<string>("redo")}
            disabled={this.manager.redoStates.length < 1}
            onClick={this.redo}
          >
            <RedoIcon fontSize="small"/>
          </IconButton>
          <IconButton
            title={t<string>("config")}
            onClick={() => this.setState({dialogOpen: "config"})}
          >
            <ConfigIcon fontSize="small"/>
          </IconButton>
        </div>
        <div>
          {groups.map((group, i) => {
            return (
              <div
                className={classes.itemGroupContainer}
                key={group.id}
                id={`itemGroup-${group.id}`}
                style={{width}}
              >
                <div className={classes.itemGroup}>
                  <NameEditor
                    item={group}
                    onChange={this.onChangeGroupName}
                    maxLength={maxGroupNameLength}
                  />
                  <div className={classes.categoriesSelector}>
                    {(()=>{
                      if(bindPropCategory !== undefined && Array.isArray(categories) && categories.length > 0){
                        return (
                          <select value={(group[bindPropCategory] as string)} onChange={this.onSelectCategory(group)}>
                            {categories.map(c => {
                              return (
                                <option key={c.id} value={c.id}>
                                  {c.label}
                                </option>
                              );
                            })}
                          </select>
                        );
                      }
                      
                      return null;
                    })()}
                  </div>
                  <div style={{flex: "auto"}} />
                  <IconButton
                    className={classes.upIcon}
                    onClick={() => this.onClickTop(group.id)}
                    disabled={i === 0}
                    disableRipple={true}
                  >
                    <ToTopIcon size={20}/>
                  </IconButton>
                  <IconButton
                    className={classes.upIcon}
                    onClick={() => this.onClickUp(group.id)}
                    disabled={i === 0}
                    disableRipple={true}
                  >
                    <UpIcon size={20}/>
                  </IconButton>
                  <IconButton
                    className={classes.downIcon}
                    onClick={() => this.onClickDown(group.id)}
                    disabled={i === groups.length - 1}
                    disableRipple={true}
                  >
                    <DownIcon size={20}/>
                  </IconButton>
                  <IconButton
                    className={classes.downIcon}
                    onClick={() => this.onClickBottom(group.id)}
                    disabled={i === groups.length - 1}
                    disableRipple={true}
                  >
                    <ToBottomIcon size={20}/>
                  </IconButton>
                  <IconButton
                    className={classes.closeIcon}
                    onClick={() => this.onClickRemoveGroup(group.id)}
                  >
                    <CloseIcon size={20}/>
                  </IconButton>
                </div>
                <div className={classes.itemsContainer}>
                  {group.items.map(item => {
                    const ref = React.createRef<SortableItem>();
                    
                    const sortableItem = (
                      <SortableItem
                        classes={classes}
                        item={item}
                        key={item.id}
                        ref={ref}
                        onChange={this.onChangeItemName}
                        onRemoveButtonClicked={this.onClickRemoveItem}
                        maxLength={maxItemNameLength}
                        currencies={currencies}
                        onChangeCurrency={this.onChangeCurrency}
                      />
                    );
                    
                    this.sortableItemRefs.push({
                      item,
                      ref,
                    });
                    
                    return (
                      <div className={classes.itemContainer} key={item.id}>
                        {sortableItem}
                      </div>
                    );
                  })}
  
                  <IconButton
                    className={classes.addIcon}
                    onClick={() => this.onAddItem(group)}
                  >
                    <AddIcon fontSize="small"/>
                  </IconButton>
                </div>
              </div>
            );
          })}
          
          <div className={classes.addItemGroup}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.addItemGroupButton}
              onClick={this.onClickAddGroup}
            >
              <AddIcon />
              {addGroupLabel}
            </Button>
          </div>
        </div>
        {dialog}
      </div>
    );
  }
  
  public componentDidMount(){
    const {useWindowAsScrollContainer} = this.props;
    const container = this.getContainer();
    
    window.addEventListener("keydown", this.onKeyDownOnWindow, false);
  
    Promise.resolve(container).then((containerNode) => {
      if(containerNode === null){
        throw new Error("containerNode not found");
      }
      
      this.containers.container = containerNode;
      this.containers.document = containerNode.ownerDocument || document;
    
      const contentWindow =
        this.props.contentWindow || this.containers.document.defaultView || window;
    
      this.containers.contentWindow =
        typeof contentWindow === "function" ? contentWindow() : this.containers.document.defaultView || window;
      
      this.containers.scrollContainer = (useWindowAsScrollContainer
        ? this.containers.document.scrollingElement || this.containers.document.documentElement
        : this.containers.container) as Element;
      
      for(const key in this.events){
        if (this.events.hasOwnProperty(key)) {
          events[(key as eventKey)].forEach((eventName) => {
            if (this.containers.container === null) {
              return;
            }
  
            this.containers.container.addEventListener(
              eventName,
              (this.events[(key as eventKey)]),
              false,
            );
          });
        }
      }
  
      this.inspectGroupAreaOffset();
    });
  }
  
  public componentDidUpdate(){
    this.inspectGroupAreaOffset();
  }
  
  public componentWillUnmount(){
    window.removeEventListener("keydown", this.onKeyDownOnWindow);
    
    if (this.containers.container) {
      for (const key in this.events) {
        if (this.events.hasOwnProperty(key)) {
          events[(key as eventKey)].forEach((eventName) => {
            if(this.containers.container !== null){
              this.containers.container.removeEventListener(eventName, this.events[(key as eventKey)]);
            }
          });
        }
      }
    }
  }
  
  public getDialog(dialogOpen: string){
    const {
      lang,
      classes,
      i18n,
      removeConfirmText,
      removeGroupConfirmText,
      reasonForNotRemovingItems,
      reasonForNotRemovingGroups,
    } = this.props;
    const {
      removingItemIds,
      removingGroupIds,
    } = this.state;
    const t = i18n.getFixedT(lang, "Finance");
    
    let dialog;
    if(dialogOpen === "remove"){
      dialog = (
        <ConfirmDialog
          onClickOK={this.onClickRemoveDialogOK}
          onClickCancel={this.onClickRemoveDialogCancel}
          lang={lang}
          title={removeConfirmText}
        >
          <div className={classes.chipRoot}>
            {removingItemIds.map(itemId => {
              const item = this.manager.getItem(itemId);
              const label = item ? item.name : "?";
              return (
                <Chip
                  className={classes.chip}
                  key={itemId}
                  label={label}
                />
              );
            })}
          </div>
          <div>
            <FormControlLabel
              label={t<string>("dontShowAgain")}
              control={
                <Checkbox
                  checked={!this.state.confirmRemove}
                  onChange={e => this.setState({confirmRemove: !e.target.checked})}
                />
              }
            />
          </div>
        </ConfirmDialog>
      );
    }
    else if(dialogOpen === "cannotRemove"){
      dialog = (
        <ConfirmDialog
          onClickOK={this.onClickRemoveDialogCancel}
          lang={lang}
          alert={true}
        >
          {(()=>{
            if(typeof(reasonForNotRemovingItems) !== "function"){
              return null;
            }
          
            const removingItems = removingItemIds
              .map(itemId => this.manager.getItem(itemId))
              .filter(item => item !== undefined) as GeneralItem[];
          
            return reasonForNotRemovingItems(removingItems);
          })()}
        </ConfirmDialog>
      );
    }
    else if(dialogOpen === "removeGroup"){
      dialog = (
        <ConfirmDialog
          onClickOK={this.onClickRemoveGroupDialogOK}
          onClickCancel={this.onClickRemoveDialogCancel}
          lang={lang}
          title={removeGroupConfirmText}
        >
          <div className={classes.chipRoot}>
            {removingGroupIds.map(groupId => {
              const group = this.manager.getGroup(groupId);
              const label = group ? group.name : "?";
            
              return (
                <Chip className={classes.chip} key={groupId} label={label} />
              );
            })}
          </div>
          <div>
            <FormControlLabel
              label={t<string>("dontShowAgain")}
              control={
                <Checkbox
                  checked={!this.state.confirmRemove}
                  onChange={e => this.setState({confirmRemove: !e.target.checked})}
                />
              }
          
            />
          </div>
        </ConfirmDialog>
      );
    }
    else if(dialogOpen === "cannotRemoveGroup"){
      dialog = (
        <ConfirmDialog
          onClickOK={this.onClickRemoveDialogCancel}
          lang={lang}
          alert={true}
        >
          {(()=>{
            if(typeof(reasonForNotRemovingGroups) !== "function"){
              return null;
            }
          
            const removingGroups = removingGroupIds
              .map(groupId => this.manager.getGroup(groupId))
              .filter(group => group) as GeneralGroup[];
          
            return reasonForNotRemovingGroups(removingGroups, this.manager.getItems());
          })()}
        </ConfirmDialog>
      );
    }
    else if(dialogOpen === "config"){
      dialog = (
        <ConfirmDialog
          onClickOK={() => this.setState({dialogOpen: ""})}
          lang={lang}
          title={t<string>("config")}
          alert={true}
        >
          <FormControlLabel
            label={t<string>("confirmDelete")}
            control={
              <Checkbox
                checked={this.state.confirmRemove}
                onChange={e => this.setState({confirmRemove: e.target.checked})}
              />
            }
          />
        </ConfirmDialog>
      );
    }
    
    return dialog;
  }
  
  public inspectGroupAreaOffset(){
    const {classes} = this.props;
  
    const itemGroupContainers = document.querySelectorAll(`.${classes.itemGroupContainer}`);
    
    itemGroupContainers.forEach((itemGroup) => {
      const element = itemGroup as HTMLElement;
      const id = +element.id.replace("itemGroup-", "");
      
      this.vars.groupsOffset[id] = {
        top: element.offsetTop,
        left: element.offsetLeft,
        width: element.offsetWidth,
        height: element.offsetHeight,
      };
    });
  }
  
  public getGroupIdAt({top, left}: {top: number, left: number}, padding: number){
    const {groupsOffset} = this.vars;
  
    const result = Object.entries(groupsOffset).find(([groupId, position]) => {
      let {top: t, left: l, width: w, height: h} = position;
      
      t -= padding;
      h += padding*2;
      l -= padding;
      w += padding*2;
  
      return (t <= top && top <= t + h)
        && (l <= left && left <= l + w);
    });
  
    if(result){
      return +result[0];
    }
    
    return null;
  }
  
  public getNodeByItemId(itemId: number){
    const {sortableItemRefs} = this;
    const item = sortableItemRefs.find(r => r.item.id === itemId);
    
    return item ? item.ref.current : null;
  }
  
  public getGroupContainerById(groupId: number){
    return document.querySelector(`#itemGroup-${groupId}`);
  }
  
  public removeHighlightFromGroups(){
    const {classes} = this.props;
    
    if(this.containers.container === null){
      return;
    }
  
    this.containers.container.querySelectorAll(`.${classes.highlightedGroup}`).forEach((el) => {
      (el as HTMLElement).classList.remove(classes.highlightedGroup);
    });
  
    this.containers.container.querySelectorAll(`.${classes.itemGroupContainer}`).forEach((el) => {
      (el as HTMLElement).style.height = "";
    });
  
    this.containers.container.querySelectorAll(`.${classes.addIcon}`).forEach(el => {
      (el as HTMLElement).style.visibility = "";
    });
  }
  
  public onChangeItemName(id: number, name: string){
    const {maxItemNameLength, onChange} = this.props;
    
    if(typeof(maxItemNameLength) === "number"){
      name = name.substr(0, maxItemNameLength);
    }
    
    this.manager.renameItem(id, name);
    
    const groups = this.manager.getGroupsWithChildren();
    this.setState({groups}, () => {
      onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onChangeCurrency(id: number, currencyId: number){
    const {onChange} = this.props;
  
    this.manager.changeCurrency(id, currencyId);
  
    const groups = this.manager.getGroupsWithChildren();
    this.setState({groups}, () => {
      onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onClickRemoveItem(id: number){
    const {onRemoveItem} = this.props;
    const {confirmRemove} = this.state;
    
    if(!confirmRemove){
      this.setState({
        removingItemIds: [id],
      }, () => {
        this.onClickRemoveDialogOK();
      });
      
      return;
    }
    
    if(typeof(onRemoveItem) === "function"){
      const item = this.manager.getItem(id);
      
      if(!item || onRemoveItem(item) !== true){
        this.setState({dialogOpen: "cannotRemove", removingItemIds: [id]});
        return;
      }
    }
    
    this.setState({dialogOpen: "remove", removingItemIds: [id]});
  }
  
  public onClickRemoveDialogOK(){
    const {onRemoveItem, onChange} = this.props;
    const {removingItemIds} = this.state;
    
    if(typeof(onRemoveItem) === "function"){
      const items = this.manager.getItems();
      const idNotRemovable = removingItemIds.find(id => {
        const item = items.find(i => i.id === id);
        if(item === undefined){
          return true;
        }
        return onRemoveItem(item) !== true;
      });
      if(typeof(idNotRemovable) === "number"){
        this.setState({dialogOpen: "cannotRemove", removingItemIds: [idNotRemovable]});
        return;
      }
    }
    
    this.manager.deleteItems(removingItemIds);
    const groups = this.manager.getGroupsWithChildren();
    this.setState({dialogOpen: "", removingItemIds: [], groups}, () => {
      onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onClickRemoveDialogCancel(){
    this.setState({dialogOpen: "", removingItemIds: []});
  }
  
  public onKeyDownOnWindow(e: KeyboardEvent){
    if((e.code === "KeyZ") && e.ctrlKey){
      this.undo();
    }
    else if((e.code === "KeyY") && e.ctrlKey){
      this.redo();
    }
  }
  
  public onAddItem(itemGroup: GeneralGroup){
    const {bindPropChild, generateItemTemplate, onChange} = this.props;
    const {groups} = this.state;
    let nameIndex = 1;
    
    const group = groups.find(g => g.id === itemGroup.id);
    if(!group){
      return;
    }
    
    const {items} = group;
    items.forEach(itm => {
      if(itm.name === `${itemGroup.name}-${nameIndex}`){
        nameIndex++;
      }
    });
  
    const name = `${itemGroup.name}-${nameIndex}`;
    
    let item = generateItemTemplate();
    item = {
      ...item,
      name,
      [bindPropChild]: itemGroup.id,
    };
    
    this.manager.addItem((item as GeneralItem));
    const newGroups = this.manager.getGroupsWithChildren();
    
    this.setState({groups: newGroups}, () => {
      onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public undo(){
    if(this.manager.undo()){
      const groups = this.manager.getGroupsWithChildren();
      this.setState({groups}, () => {
        this.props.onChange(this.manager.getItems(), this.manager.getGroups());
      });
    }
  }
  
  public redo(){
    if(this.manager.redo()){
      const groups = this.manager.getGroupsWithChildren();
      this.setState({groups}, () => {
        this.props.onChange(this.manager.getItems(), this.manager.getGroups());
      });
    }
  }
  
  public onChangeGroupName(id: number, name: string){
    const {maxGroupNameLength, onChange} = this.props;
  
    if(typeof(maxGroupNameLength) === "number"){
      name = name.substr(0, maxGroupNameLength);
    }
  
    this.manager.renameGroup(id, name);
    const groups = this.manager.getGroupsWithChildren();
    
    this.setState({groups}, () => {
      onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onClickRemoveGroup(id: number){
    const {onRemoveGroup} = this.props;
    const {confirmRemove} = this.state;
  
    if(!confirmRemove){
      this.setState({
        removingGroupIds: [id],
      }, () => {
        this.onClickRemoveGroupDialogOK();
      });
    
      return;
    }
  
    if(typeof(onRemoveGroup) === "function" && onRemoveGroup(id, this.manager.getItems(), this.manager.getGroups()) !== true){
      this.setState({dialogOpen: "cannotRemoveGroup", removingGroupIds: [id]});
    }
    else{
      this.setState({dialogOpen: "removeGroup", removingGroupIds: [id]});
    }
  }
  
  public onClickRemoveGroupDialogOK(){
    const {onRemoveGroup, onChange} = this.props;
    const {removingGroupIds} = this.state;
  
    if(typeof(onRemoveGroup) === "function"){
      const idNotRemovable = removingGroupIds.find(id => onRemoveGroup(id, this.manager.getItems(), this.manager.getGroups()) !== true);
      if(typeof(idNotRemovable) === "number"){
        this.setState({dialogOpen: "cannotRemoveGroup", removingGroupIds: [idNotRemovable]});
        return;
      }
    }
  
    this.manager.deleteGroups(removingGroupIds);
    const groups = this.manager.getGroupsWithChildren();
    this.setState({dialogOpen: "", removingGroupIds: [], groups}, () => {
      onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onClickAddGroup(){
    const {generateGroupTemplate, onChange} = this.props;
    let {groups} = this.state;
  
    const group = generateGroupTemplate();
    let nameIndex: number|null = null;
    const newGroupName = group.name || "Group";
    
    groups.forEach(g => {
      if(nameIndex === null && g.name === group.name){
        nameIndex = 1;
        group.name = `${newGroupName} ${nameIndex}`;
      }
      if(typeof(nameIndex) === "number" && g.name === group.name){
        nameIndex++;
        group.name = `${newGroupName} ${nameIndex}`;
      }
    });
    
    this.manager.addGroup((group as GeneralGroup));
    groups = this.manager.getGroupsWithChildren();
    
    this.setState({groups}, () => {
      onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onClickUp(groupId: number){
    let groups = this.manager.getGroups();
  
    groups = [...groups];
    groups.sort((a, b) => a.order - b.order);
    const index = groups.findIndex(g => g.id === groupId);
  
    let insertAt;
  
    if(index - 1 >= 0){
      insertAt = groups[index - 1].id;
    }
    else{
      insertAt = groups[0].id;
    }
    
    this.manager.changeGroupOrder(groupId, insertAt);
    const newGroups = this.manager.getGroupsWithChildren();
    
    this.setState({groups: newGroups}, () => {
      this.props.onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onClickDown(groupId: number){
    let groups = this.manager.getGroups();
  
    groups = [...groups];
    groups.sort((a, b) => a.order - b.order);
    const index = groups.findIndex(g => g.id === groupId);
  
    let insertAt;
  
    if(index + 2 <= groups.length - 1){
      insertAt = groups[index + 2].id;
    }
    else{
      insertAt = null;
    }
  
    this.manager.changeGroupOrder(groupId, insertAt);
    const newGroups = this.manager.getGroupsWithChildren();
  
    this.setState({groups: newGroups}, () => {
      this.props.onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onClickTop(groupId: number){
    let {groups} = this.state;
  
    const topGroup = groups.reduce((acc, g) => {
      if(acc.order >= g.order){
        return g;
      }
      
      return acc;
    });
    
    this.manager.changeGroupOrder(groupId, topGroup.id);
    groups = this.manager.getGroupsWithChildren();
    
    this.setState({groups}, () => {
      this.props.onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onClickBottom(groupId: number){
    this.manager.changeGroupOrder(groupId, null);
    const groups = this.manager.getGroupsWithChildren();
    
    this.setState({groups}, () => {
      this.props.onChange(this.manager.getItems(), this.manager.getGroups());
    });
  }
  
  public onSelectCategory(itemGroup: GeneralGroup){
    return (e: React.ChangeEvent<HTMLSelectElement>) => {
      const category = e.target.value;
      this.manager.changeCategoryOfGroup(itemGroup.id, category);
      const groups = this.manager.getGroupsWithChildren();
      
      this.setState({groups}, () => {
        this.props.onChange(this.manager.getItems(), this.manager.getGroups());
      });
    };
  }
  
  public shouldCancelStart(event: Event){
    // Cancel sorting if the event target is an `input`, `textarea`, `select` or `option`
    const disabledElements = [
      "input",
      "textarea",
      "select",
      "option",
      "button",
    ];
    
    const target = event.target as HTMLElement;
    if(!target){
      return true;
    }
    
    return disabledElements.indexOf(target.tagName.toLowerCase()) !== -1;
  }
  
  public getHelperDimensions({node}: {node: HTMLElement}){
    return {
      width: node.offsetWidth,
      height: node.offsetHeight,
    };
  }
  
  public handleStart(e: Event){
    return _handleStart.call(this, e);
  }
  
  public handleMove(e: Event){
    return _handleMove.call(this, e);
  }
  
  public handleEnd(e: Event){
    return _handleEnd.call(this, e);
  }
  
  public handlePress(e: Event){
    return _handlePress.call(this, e);
  }
  
  public cancel(){
    return _cancel.call(this);
  }
  
  public handleSortMove(e: Event){
    return _handleSortMove.call(this, e);
  }
  
  public handleSortEnd(e: Event){
    return _handleSortEnd.call(this, e);
  }
  
  public getLockPixelOffsets(){
    return _getLockPixelOffsets.call(this);
  }
  
  public updatePosition(e: Event){
    return _updatePosition.call(this, e);
  }
  
  public animateNodes(){
    return _animateNodes.call(this);
  }
  
  public autoScroll(){
    return _autoScroll.call(this);
  }
  
  public getContainer(){
    return _getContainer.call(this);
  }
  
  public static defaultProps = {
    transitionDuration: 300,
    useWindowAsScrollContainer: true,
    lockToContainerEdges: false,
    lockOffset: "50%",
  };
}





const styles: StyleRulesCallback = (theme: CustomTheme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    userSelect: "none",
  },
  itemGroupContainer: {
    marginBottom: theme.spacing.unit*4,
  },
  itemGroup: {
    "display": "flex",
    "alignItems": "center",
    "& > div:first-child": {
      fontSize: "1.3rem",
      cursor: "pointer",
      border: "1px solid transparent",
      borderBottom: `2px dashed transparent`,
      height: "1.6rem",
      lineHeight: "1.6rem",
      padding: 0,
      margin: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& > div:nth-child(2)": {
      color: "inherit",
    },
    "& > input": {
      fontSize: "1.3rem",
      border: "1px solid transparent",
      borderBottom: `2px dashed ${theme.palette.text.secondary}`,
      color: "inherit",
      background: "transparent",
      padding: 0,
      margin: 0,
      height: "1.6rem",
      lineHeight: "1.6rem",
    },
    "& > input:focus": {
      outline: "none",
    },
  },
  itemsContainer: {
    marginTop: theme.spacing.unit,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  itemContainer: {
    padding: theme.spacing.unit/2,
  },
  item: {
    "border": `1px solid ${theme.palette.text.secondary}`,
    "fontSize": ".85rem",
    "padding": "3px 7px",
    "overflow": "hidden",
    "whiteSpace": "nowrap",
    "display": "flex",
    "alignItems": "center",
    "& input": {
      border: "1px solid transparent",
      borderBottom: `1px dashed ${theme.palette.text.secondary}`,
      color: "inherit",
      background: "transparent",
      maxWidth: 140,
    },
    "& input:focus": {
      outline: "none",
    },
  },
  checkboxContainer: {
    marginTop: theme.spacing.unit*2,
    marginBottom: theme.spacing.unit*2,
    padding: theme.spacing.unit,
    border: `1px solid ${theme.palette.text.secondary}`,
    borderRadius: 5,
  },
  helper: {
    position: "relative",
    zIndex: 1,
    backgroundColor: theme.palette.text.secondary,
    color: theme.palette.primary.main,
    opacity: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  highlightedGroup: {
    backgroundColor: theme.palette.text.secondary,
  },
  grabber: {
    marginRight: theme.spacing.unit/2,
    cursor: "move",
  },
  grabberIcon: {
    marginLeft: -4,
    display: "flex",
    alignItems: "center",
  },
  itemName: {
    "cursor": "pointer",
    "& > div": {
      lineHeight: "24px",
      maxWidth: 200,
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
  },
  currency: {
    "marginLeft": 12,
    "& select": {
      background: (theme.custom && theme.custom.brightness === "dark") ? theme.palette.primary.main : "",
      color: (theme.custom && theme.custom.brightness === "dark") ? theme.palette.primary.contrastText : "",
      padding: "1px 8px",
      margin: 0,
    },
    "& select:-moz-focusring": {
      color: "transparent",
      textShadow: `0 0 0 ${theme.palette.text.primary}`,
    },
    "& option": {
      background: (theme.custom && theme.custom.brightness === "dark") ? theme.palette.secondary.main : "",
      color: (theme.custom && theme.custom.brightness === "dark") ? theme.palette.primary.contrastText : "",
    },
  },
  currencySelect: {
  },
  closeIcon: {
    marginLeft: 12,
    fontSize: ".8rem",
    padding: 4,
  },
  upIcon: {
    fontSize: ".8rem",
    padding: 4,
  },
  downIcon: {
    fontSize: ".8rem",
    padding: 4,
  },
  closeIconSmall: {
    marginLeft: 12,
    padding: 4,
    color: theme.palette.error.main,
  },
  addIcon: {
    marginLeft: 8,
    padding: 4,
    fontSize: ".8rem",
  },
  toolbar: {
    marginBottom: theme.spacing.unit*2,
    width: "100%",
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.text.secondary}`,
    paddingTop: theme.spacing.unit/2,
    paddingBottom: theme.spacing.unit/2,
  },
  removingItemContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  removingItem: {
    margin: 4,
    padding: "4px 8px",
    borderRadius: 1,
    border: `1px solid ${theme.palette.text.primary}`,
  },
  addItemGroup: {
    marginBottom: theme.spacing.unit*3,
  },
  addItemGroupButton: {
    marginRight: theme.spacing.unit,
  },
  categoriesSelector: {
    "marginLeft": 12,
    "& select": {
      background: (theme.custom && theme.custom.brightness === "dark") ? theme.palette.secondary.main : "",
      color: (theme.custom && theme.custom.brightness === "dark") ? theme.palette.primary.contrastText : "",
      padding: "3px 8px",
      margin: 0,
    },
    "& select:-moz-focusring": {
      color: "transparent",
      textShadow: `0 0 0 ${theme.palette.text.primary}`,
    },
    "& option": {
      background: (theme.custom && theme.custom.brightness === "dark") ? theme.palette.secondary.main : "",
      color: (theme.custom && theme.custom.brightness === "dark") ? theme.palette.primary.contrastText : "",
    },
  },
  chipsRoot: {
    display: "flex",
    flexWrap: "wrap",
    padding: theme.spacing.unit / 2,
  },
  chip: {
    margin: theme.spacing.unit / 2,
  },
});



let _Component: React.ComponentType<any> = GroupEditor;
_Component = withStyles(styles, { withTheme: true })(_Component);
_Component = withTranslation("Home")(_Component);
_Component = withScrollAPI(_Component);

const ComponentWithHoc = _Component as React.ComponentType<Omit<GroupEditorProps, TReservedHocProps | "scrollAPI" | "lang">>;

const mapStateToProps = (rootReduxState: RootState) => {
  const {lang} = rootReduxState.App;
  return {lang};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, undefined, A.RootActions>) => {
  return {
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ComponentWithHoc);
